import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Clock, Users, Car, X, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHeader, Input, Labeled } from "./Rentals";
import { DatePicker, TimePicker, combineDateTime } from "../lib/date-pickers";
import { BookingSuccessDialog } from "../lib/booking-success";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";
import { formatISK } from "../lib/currency";
import { applySeo } from "../lib/seo";

const fmtIsk = (n) => formatISK(n);

export const TOUR_FALLBACK_IMAGES = {
  "blue-lagoon-private-experience": "https://images.pexels.com/photos/346972/pexels-photo-346972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  "golden-circle": "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/fuw8993x_image.png",
  "south-coast": "https://images.unsplash.com/photo-1637076217138-2b3c86d67846?auto=format&fit=crop&w=1400&q=75",
  "private-northern-lights-experience": "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/tu9sqio5_image.png",
  "northern-lights": "https://customer-assets.emergentagent.com/job_b7222ed5-91f4-4808-a840-bb97b21f35ac/artifacts/tu9sqio5_image.png",
  "snaefellsnes-peninsula": "https://images.unsplash.com/photo-1559233622-34ab91b3c046?auto=format&fit=crop&w=1400&q=75",
  "reykjanes-blue-lagoon": "https://images.pexels.com/photos/346972/pexels-photo-346972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
};

export function formatDuration(minutes) {
  const total = Number(minutes || 0);
  if (!total) return null;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m} min`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

function defaultPackage(pax, tour) {
  const p = Math.max(1, Number(pax) || 1);
  if (p >= 5 && tour.supports_8pack) return "8pack";
  if (p <= 4 && tour.supports_4pack) return "4pack";
  if (tour.supports_8pack) return "8pack";
  return "4pack";
}

export function resolvePrice(tour, pkg, pax) {
  // Package-capacity bookings take precedence
  if (pkg === "4pack" && tour.price_4pack != null) {
    return { total: tour.price_4pack, label: fmtIsk(tour.price_4pack), detail: "Up to 4 passengers · any car" };
  }
  if (pkg === "8pack" && tour.price_8pack != null) {
    return { total: tour.price_8pack, label: fmtIsk(tour.price_8pack), detail: "Up to 8 passengers · 9-seat van" };
  }
  const p = Math.max(1, Number(pax) || 1);
  switch (tour.price_type) {
    case "per_person": {
      const total = Number(tour.base_price || 0) * p;
      return { total, label: fmtIsk(total), detail: `${fmtIsk(tour.base_price)} × ${p} pax` };
    }
    case "per_group":
    case "fixed":
      return { total: Number(tour.base_price || 0), label: fmtIsk(tour.base_price), detail: "Flat price for the group" };
    default:
      return { total: Number(tour.base_price || 0), label: fmtIsk(tour.base_price), detail: null };
  }
}

// ==================================================
export default function Tours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    applySeo({
      title: "IceFleet Tours — Private Iceland Tours",
      description: "Explore private Iceland tours from IceFleet, with full itineraries, highlights, and direct booking options.",
      canonicalPath: "/tours",
    });
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/tours")
      .then((r) => alive && setTours(Array.isArray(r.data) ? r.data : []))
      .catch(() => alive && setTours([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Tours"
        title="Private day tours and custom itineraries."
        subtitle="Small groups, no crowds. Always private, always direct."
      />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 grid gap-6 md:grid-cols-3" data-testid="tours-grid">
        {loading && <div className="md:col-span-3 text-center py-12 text-slate-500">Loading tours…</div>}
        {!loading && tours.length === 0 && (
          <div data-testid="tours-empty" className="md:col-span-3 rounded-xl border border-slate-200 bg-slate-50/60 p-10 text-center text-slate-500">
            No tours are currently published. Message us on WhatsApp and we'll design one for you.
          </div>
        )}
        {tours.map((t) => <TourCard key={t.id} tour={t} />)}
      </section>
    </div>
  );
}

function TourCard({ tour }) {
  const duration = formatDuration(tour.estimated_display_minutes);
  const startPrice = tour.supports_4pack && tour.price_4pack != null
    ? tour.price_4pack
    : tour.supports_8pack && tour.price_8pack != null
    ? tour.price_8pack
    : tour.base_price;
  const image = tour.featured_image || TOUR_FALLBACK_IMAGES[tour.slug] || "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=75";

  return (
    <article
      className="group rounded-xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 transition-transform"
      data-testid={`tour-card-${tour.slug || tour.id}`}
    >
      <Link to={`/tours/${tour.slug}`} data-testid={`tour-card-link-${tour.slug || tour.id}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
        <div className="aspect-[4/3] overflow-hidden bg-slate-100">
          <img src={image} alt={tour.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" decoding="async" sizes="(min-width: 768px) 33vw, 100vw" />
        </div>
      </Link>
      <div className="p-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-bold uppercase tracking-widest text-sky-700">
          {duration && <span className="inline-flex items-center gap-1"><Clock size={12} /> {duration}</span>}
          {tour.max_passengers && <span className="inline-flex items-center gap-1 text-slate-500"><Users size={12} /> up to {tour.max_passengers}</span>}
        </div>
        <Link to={`/tours/${tour.slug}`} data-testid={`tour-title-link-${tour.slug || tour.id}`} className="mt-2 block font-heading text-xl font-semibold text-slate-900 hover:text-sky-700">
          {tour.title}
        </Link>
        {tour.short_description && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{tour.short_description}</p>}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {tour.supports_4pack && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-semibold">
              <Car size={10} /> 4pack {tour.price_4pack != null ? `· ${fmtIsk(tour.price_4pack)}` : ""}
            </span>
          )}
          {tour.supports_8pack && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px] font-semibold">
              <Users size={10} /> 8pack {tour.price_8pack != null ? `· ${fmtIsk(tour.price_8pack)}` : ""}
            </span>
          )}
        </div>

        <div className="mt-4 flex items-baseline gap-2">
          <p className="font-heading text-lg font-semibold text-slate-900 tabular-nums" data-testid={`tour-price-${tour.slug || tour.id}`}>
            from {fmtIsk(startPrice)}
          </p>
        </div>
        <Link
          to={`/tours/${tour.slug}`}
          data-testid={`tour-book-${tour.slug || tour.id}`}
          className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Select &amp; book
        </Link>
      </div>
    </article>
  );
}

export function TourBookingDialog({ tour, onClose, onCreated }) {
  const today = new Date();
  const departureOptions = useMemo(() => (tour.allowed_start_times?.length ? tour.allowed_start_times : [tour.earliest_start_time || "09:00"]), [tour]);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    date: today,
    time: departureOptions[0],
    passengers: 2,
    notes: "",
  });
  const [pkg, setPkg] = useState(() => defaultPackage(2, tour));
  const [avail, setAvail] = useState(null);
  const [smartOptions, setSmartOptions] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);

  // Auto-update package when passenger count changes, unless user has already manually picked
  const [userTouchedPkg, setUserTouchedPkg] = useState(false);
  useEffect(() => {
    if (userTouchedPkg) return;
    setPkg(defaultPackage(form.passengers, tour));
  }, [form.passengers, tour, userTouchedPkg]);

  const price = useMemo(() => resolvePrice(tour, pkg, form.passengers), [tour, pkg, form.passengers]);
  const maxPax = tour.max_passengers || 16;
  const supportsBoth = tour.supports_4pack && tour.supports_8pack;

  // Live availability check (debounced via effect)
  useEffect(() => {
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const start = combineDateTime(form.date, form.time);
        const { data } = await api.post(`/tours/${tour.id}/availability-check`, {
          start_at: start.toISOString(),
          passengers: Number(form.passengers) || 1,
          tour_package_type: pkg,
        });
        setAvail(data);
        if (!data.available && Array.isArray(data.next_available_options) && data.next_available_options.length > 0) {
          const recommended = data.next_available_options[0];
          setSmartOptions({
            message: data.message,
            options: data.next_available_options,
            recommendedStart: recommended.start,
          });
          const currentIso = start.toISOString();
          if (recommended.start !== currentIso) {
            applySlot(recommended.start, recommended.display_time);
          }
        } else if (!data.available) {
          setSmartOptions({ message: data.message, options: [], recommendedStart: null });
        }
      } catch (e) {
        setAvail(null);
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.date, form.time, form.passengers, pkg, tour.id]);

  const selectPkg = (p) => {
    setUserTouchedPkg(true);
    setPkg(p);
  };

  const applySlot = (slot, displayTime) => {
    const d = new Date(slot);
    const time = displayTime || `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    setForm({ ...form, date: d, time });
  };

  const smartAvailability = useMemo(() => {
    if (!smartOptions) return avail;
    const currentStart = combineDateTime(form.date, form.time)?.toISOString();
    const matchesSuggested = smartOptions.options?.some((option) => option.start === currentStart);
    if (!matchesSuggested) return avail;
    return {
      available: false,
      message: `Sorry, this departure is not available. We selected the next available tour time for you.`,
      next_available_options: smartOptions.options,
      next_available_start: smartOptions.recommendedStart,
      next_available_display: smartOptions.options?.[0]?.display_time,
    };
  }, [avail, smartOptions, form.date, form.time]);

  const submit = async (e) => {
    e.preventDefault();
    if (avail && !avail.available) {
          toast.error("Please pick an available time slot before submitting");
      return;
    }
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBusy(true);
    try {
      const start = combineDateTime(form.date, form.time);
      const durationMinutes = (Number(tour.driving_minutes) || 0) + (Number(tour.stopping_minutes) || 0);
      const end = new Date(start.getTime() + Math.max(durationMinutes, 60) * 60 * 1000);

      const { data } = await api.post("/bookings", {
        service_type: "tours",
        booking_type: "tour",
        tour_id: tour.id,
        tour_package_type: pkg,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        tour_name: tour.title,
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        preferred_contact_method: form.preferred_contact_method || null,
        passengers: Number(form.passengers) || 1,
        notes: form.notes || null,
      });
      if (data.invoice_mode === "online_card" && data.status === "pending_payment" && data.pricing_mode === "fixed_price") {
        const checkout = await api.post("/payments/checkout", {
          booking_id: data.id,
          origin_url: window.location.origin,
        });
        sessionStorage.setItem("icefleet_pay_booking", data.id);
        window.location.href = checkout.data.url;
        return;
      }
      onCreated && onCreated(data);
      toast.success("Tour request submitted — we'll confirm shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Booking failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/70 p-0 sm:p-6"
      role="dialog"
      onClick={onClose}
      data-testid="tour-booking-dialog"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="tour-booking-form"
        className="w-full max-w-lg rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-sky-700">Request tour</p>
            <h3 className="font-heading text-2xl font-semibold mt-1">{tour.title}</h3>
            {formatDuration(tour.estimated_display_minutes) && (
              <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                <Clock size={12} /> {formatDuration(tour.estimated_display_minutes)}
                {tour.max_passengers && (
                  <>
                    <span className="mx-1 text-slate-300">·</span>
                    <Users size={12} /> up to {tour.max_passengers} pax
                  </>
                )}
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="close" className="text-slate-500" data-testid="tour-form-close">
            <X size={18} />
          </button>
        </div>

        {/* Package selector */}
        {supportsBoth && (
          <div className="grid grid-cols-2 gap-2" data-testid="tour-pkg-row">
            <PackageBtn
              testid="tour-pkg-4pack"
              selected={pkg === "4pack"}
              onClick={() => selectPkg("4pack")}
              title="4pack"
              sub="Up to 4 pax · any car"
              price={tour.price_4pack}
            />
            <PackageBtn
              testid="tour-pkg-8pack"
              selected={pkg === "8pack"}
              onClick={() => selectPkg("8pack")}
              title="8pack"
              sub="Up to 8 pax · 9-seat van"
              price={tour.price_8pack}
            />
          </div>
        )}

        <Labeled label="Full name">
          <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="tour-name" />
        </Labeled>
        <Labeled label="Email">
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tour-email" />
        </Labeled>
        <Labeled label="Phone / WhatsApp">
          <PhoneField
            value={form.phone}
            onChange={(phone) => setForm({ ...form, phone })}
            countryTestId="tour-phone-country"
            numberTestId="tour-phone"
            errorTestId="tour-phone-error"
          />
        </Labeled>
        <Labeled label="Preferred contact method">
          <PreferredContactMethodField
            value={form.preferred_contact_method}
            onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })}
            testid="tour-contact-method"
          />
        </Labeled>
        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled label="Date">
            <DatePicker value={form.date} onChange={(d) => setForm({ ...form, date: d })} min={today} data-testid="tour-date" />
          </Labeled>
          <Labeled label="Departure time">
            <select data-testid="tour-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
              {departureOptions.map((time) => <option key={time} value={time}>{time}</option>)}
            </select>
          </Labeled>
        </div>
        <Labeled label="Passengers">
          <Input
            type="number"
            min="1"
            max={pkg === "4pack" ? 4 : maxPax}
            required
            value={form.passengers}
            onChange={(e) => setForm({ ...form, passengers: e.target.value })}
            data-testid="tour-pax"
          />
        </Labeled>

        {/* Availability feedback */}
        <AvailabilityBanner checking={checking} avail={smartAvailability} onSelectSlot={applySlot} />

        {/* Dynamic price box */}
        <div className="rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-4" data-testid="tour-price-banner">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">Estimated total</p>
          <p className="mt-1 font-heading text-2xl font-semibold tabular-nums text-slate-900" data-testid="tour-price-amount">
            {price.label}
          </p>
          {price.detail && <p className="text-xs text-slate-600 mt-0.5">{price.detail}</p>}
          <p className="text-[11px] text-slate-500 mt-2">VAT included · Chauffeur-driven private tour</p>
          <p className="mt-3 text-sm font-semibold text-slate-900" data-testid="tour-pay-now-copy">Secure your booking now</p>
        </div>

        <Labeled label="Anything we should know?">
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="tour-notes"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 outline-none"
          />
        </Labeled>
        <button
          type="submit"
          disabled={busy || (avail && !avail.available)}
          data-testid="tour-submit"
          className="w-full rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? "Redirecting to payment…" : `Pay & confirm booking — ${price.label}`}
        </button>
      </form>
    </div>
  );
}

function PackageBtn({ selected, onClick, title, sub, price, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-lg border px-4 py-3 transition ${
        selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:border-slate-900"
      }`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.18em]">{title}</p>
      <p className="text-[11px] opacity-80">{sub}</p>
      {price != null && (
        <p className={`mt-1 text-sm font-semibold tabular-nums ${selected ? "text-white" : "text-slate-900"}`}>
          {fmtIsk(price)}
        </p>
      )}
    </button>
  );
}

function AvailabilityBanner({ checking, avail, onSelectSlot }) {
  if (checking) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="tour-avail-checking">
        Checking availability…
      </div>
    );
  }
  if (!avail) return null;
  if (avail.available) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center gap-2" data-testid="tour-avail-ok">
        <CheckCircle2 size={14} /> Available at selected time
      </div>
    );
  }
  const options = Array.isArray(avail.next_available_options) ? avail.next_available_options : [];
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 space-y-1.5" data-testid="tour-avail-unavailable">
      <p className="flex items-center gap-2 font-medium">
        <AlertTriangle size={14} /> {avail.message || "Sorry, this departure is not available."}
      </p>
      {options.length > 0 ? (
        <div className="space-y-2" data-testid="tour-next-available-options">
          <p>Next available:</p>
          <div className="flex flex-wrap gap-2">
            {options.map((option, index) => (
              <button
                key={option.start}
                type="button"
                onClick={() => onSelectSlot(option.start, option.display_time)}
                data-testid={`tour-avail-option-${option.display_time}`}
                className={`w-full sm:w-auto rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${index === 0 ? "border-slate-900 bg-slate-900 text-white" : "border-amber-700 text-amber-900 hover:bg-amber-100"}`}
              >
                {index === 0 ? `Recommended · ${option.display_time}` : option.display_time}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p data-testid="tour-avail-fully-booked">{avail.message || "Sorry, no availability was found for the selected period."}</p>
      )}
    </div>
  );
}
